import { useState, useEffect } from "react";
import { api, QUALIFICATION_LEVELS, getLevelInfo } from "../lib/utils";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import MaintenancePanel, { useMaintenanceSelection } from "../components/MaintenancePanel";
import { Trophy, Plus, Award, Pencil, Trash2, Loader2 } from "lucide-react";

export default function Awards() {
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAward, setEditingAward] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { selectedItems, selectMode, clearSelection } = useMaintenanceSelection();
  const [formData, setFormData] = useState({
    title: "",
    qualification_level: "bachelor",
    total_credits: 120,
    description: "",
  });

  useEffect(() => {
    fetchAwards();
  }, []);

  const fetchAwards = async () => {
    try {
      const response = await api.get("/awards");
      setAwards(response.data);
    } catch (error) {
      toast.error("Failed to load awards");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingAward) {
        // Update existing award
        await api.put(`/awards/${editingAward.award_id}`, formData);
        toast.success("Award updated successfully");
      } else {
        // Create new award
        await api.post("/awards", formData);
        toast.success("Award created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchAwards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (award) => {
    setEditingAward(award);
    setFormData({
      title: award.title,
      qualification_level: award.qualification_level,
      total_credits: award.total_credits,
      description: award.description || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (awardId) => {
    try {
      await api.delete(`/awards/${awardId}`);
      toast.success("Award deleted successfully");
      fetchAwards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete award");
    }
  };

  const resetForm = () => {
    setEditingAward(null);
    setFormData({
      title: "",
      qualification_level: "bachelor",
      total_credits: 120,
      description: "",
    });
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const groupedAwards = QUALIFICATION_LEVELS.map(level => ({
    ...level,
    awards: awards.filter(a => a.qualification_level === level.value)
  }));

  // Calculate stats
  const totalAwards = awards.length;
  const awardsByLevel = QUALIFICATION_LEVELS.map(level => ({
    level: level.label,
    count: awards.filter(a => a.qualification_level === level.value).length
  }));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="awards-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Award Titles</h1>
            <p className="text-slate-500 mt-1">
              Manage qualification awards for RPL assessments. These awards can be recommended by AI and selected by assessors.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button data-testid="add-award-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Award
              </Button>
            </DialogTrigger>
            <MaintenancePanel
              entityType="awards"
              entityLabel="Awards"
              items={awards}
              idField="award_id"
              onRefresh={() => {
                fetchAwards();
                clearSelection();
              }}
              getItemLabel={(item) => item.title}
            />
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingAward ? "Edit Award" : "Create Award Title"}</DialogTitle>
                <DialogDescription>
                  {editingAward 
                    ? "Update the award details below." 
                    : "Add a new qualification award that can be recommended by AI."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Award Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Master of Science in Organizational Leadership"
                    required
                    data-testid="award-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Qualification Level *</Label>
                    <Select
                      value={formData.qualification_level}
                      onValueChange={(v) => {
                        // Auto-set credits based on level
                        const defaultCredits = {
                          diploma: 60,
                          bachelor: 120,
                          master: 180,
                          doctoral: 240
                        };
                        setFormData({ 
                          ...formData, 
                          qualification_level: v,
                          total_credits: defaultCredits[v] || 120
                        });
                      }}
                    >
                      <SelectTrigger data-testid="award-level-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {QUALIFICATION_LEVELS.map((level) => (
                          <SelectItem key={level.value} value={level.value}>
                            {level.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Total Credits *</Label>
                    <Input
                      type="number"
                      value={formData.total_credits}
                      onChange={(e) => setFormData({ ...formData, total_credits: parseInt(e.target.value) || 120 })}
                      min={30}
                      max={500}
                      required
                      data-testid="award-credits-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the award and its requirements..."
                    rows={3}
                    data-testid="award-description-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => handleDialogChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="save-award-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {editingAward ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      editingAward ? "Update Award" : "Create Award"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Trophy className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalAwards}</p>
                  <p className="text-xs text-slate-500">Total Awards</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {awardsByLevel.map((item) => (
            <Card key={item.level} className="border-slate-200">
              <CardContent className="p-4">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{item.count}</p>
                  <p className="text-xs text-slate-500">{item.level}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : awards.length > 0 ? (
          <div className="space-y-8">
            {groupedAwards.map((group) => (
              group.awards.length > 0 && (
                <div key={group.value}>
                  <div className="flex items-center gap-2 mb-4">
                    <Badge className={group.color}>{group.label}</Badge>
                    <span className="text-sm text-slate-500">({group.awards.length} awards)</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {group.awards.map((award) => (
                      <Card
                        key={award.award_id}
                        className="border-slate-200 hover:border-slate-300 transition-colors"
                        data-testid={`award-card-${award.award_id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Award className="w-5 h-5 text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-slate-900">{award.title}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline">{award.total_credits} credits</Badge>
                              </div>
                              {award.description && (
                                <p className="text-sm text-slate-500 mt-2 line-clamp-2">{award.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(award)}
                                className="text-slate-500 hover:text-blue-600"
                                data-testid={`edit-award-${award.award_id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-slate-500 hover:text-red-600"
                                    data-testid={`delete-award-${award.award_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Award?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{award.title}"? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(award.award_id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Award Titles Yet</h3>
              <p className="text-slate-500 mb-4 max-w-md mx-auto">
                Add award titles that can be recommended for candidates during RPL assessment. 
                Awards are grouped by qualification level.
              </p>
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-award-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Award
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Help Card */}
        {awards.length > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">How Awards Work</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    When you run AI analysis on a candidate, Credentia AI will recommend awards from this list based on the candidate's qualifications. 
                    Assessors can also manually select awards during the decision process. Make sure to add relevant awards for each qualification level.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
